import React, { useCallback, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { GetLinkedBookings } from "../../../../../APIs/Booking/GetLinkedBookings";
import {
  linkBooking,
  lookupBookingByRef
} from "../../../../../APIs/Booking/LinkBooking";
import { unlinkBooking } from "../../../../../APIs/Booking/UnlinkBooking";
import {
  LinkedBookingAction,
  updateLinkedBookingsStatus
} from "../../../../../APIs/Booking/UpdateLinkedBookingsStatus";
import { useDeviceContext } from "../../../../../context/DeviceContext";
import { useBookingPermissions } from "../../../../../hooks/useBookingPermissions";
import { useDiaryContext } from "../../../../../hooks/useDiaryContext";
import { useNotificationsContext } from "../../../../../hooks/useNotificationsContext";
import Loading from "../../../../../utils/Loading";
import { EDSButton } from "../../../../eds/EDSButton";
import Table from "../../../../Shared/Table/Table";
import SelectedBookingPayments from "../Payments/SelectedBookingPayments";
import { SelectedBookingTabEnum } from "../Tabs/SelectedBookingTabsController";
import BulkStatusActions from "./BulkStatusActions";
import LinkBookingDialog from "./LinkBookingDialog";
import {
  getLinkedBookingDetails,
  mapLinkedBookingsTableData
} from "./SelectedBookingLinkedController";
import UnlinkBookingDialog from "./UnlinkBookingDialog";

type SelectedBookingLinkedType = {
  setSelectedTabKey: (value: SelectedBookingTabEnum) => void;
};
const SelectedBookingLinked = ({
  setSelectedTabKey
}: SelectedBookingLinkedType) => {
  const diaryContext = useDiaryContext();
  const { connectionStatus } = useDeviceContext();
  const { userCanManageBooking } = useBookingPermissions();
  const notificationsContext = useNotificationsContext();
  const queryClient = useQueryClient();

  const selectedBookingId = diaryContext.selectedBooking?.id ?? "";
  const isOnline = connectionStatus === "internet";

  const [isLinkDialogOpen, setIsLinkDialogOpen] = useState(false);
  const [isLinking, setIsLinking] = useState(false);
  const [unlinkTarget, setUnlinkTarget] = useState<{
    bookingId: string;
    reference?: string;
  } | null>(null);
  const [isUnlinking, setIsUnlinking] = useState(false);
  const [isBulkUpdating, setIsBulkUpdating] = useState(false);

  const { data: linkedBookingsRefData, isLoading } = useQuery({
    queryKey: ["GetLinkedBookings", selectedBookingId],
    queryFn: () => GetLinkedBookings(selectedBookingId)
  });

  const { data: linkedBookingsData, isLoading: isLoading2 } = useQuery({
    queryKey: ["linkedBookingDetailsData", linkedBookingsRefData?.toString()],
    queryFn: () =>
      getLinkedBookingDetails({
        linkedBookingIds: linkedBookingsRefData,
        diaryContext
      })
  });

  const invalidateLinkedBookings = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: ["GetLinkedBookings", selectedBookingId]
    });
    queryClient.invalidateQueries({
      queryKey: ["linkedBookingDetailsData"]
    });
  }, [queryClient, selectedBookingId]);

  const handleLinkBooking = useCallback(
    async (reference: string) => {
      if (!isOnline) {
        notificationsContext.addNotification({
          heading: "Unable to link booking whilst offline",
          autoClose: 4000,
          toastType: "error"
        });
        return;
      }

      setIsLinking(true);
      try {
        const venueGroup = diaryContext.selectedBooking?.venueGroup ?? "";
        const bookingIdToLink = await lookupBookingByRef(reference, venueGroup);

        if (!bookingIdToLink) {
          notificationsContext.addNotification({
            heading: "No bookings were found matching this reference",
            autoClose: 4000,
            toastType: "error"
          });
          return;
        }

        const success = await linkBooking(selectedBookingId, bookingIdToLink);
        if (success) {
          notificationsContext.addNotification({
            heading: "Booking linked successfully",
            autoClose: 4000,
            toastType: "success"
          });
          setIsLinkDialogOpen(false);
          invalidateLinkedBookings();
        } else {
          notificationsContext.addNotification({
            heading: "Failed to link booking",
            autoClose: 4000,
            toastType: "error"
          });
        }
      } finally {
        setIsLinking(false);
      }
    },
    [
      isOnline,
      selectedBookingId,
      diaryContext.selectedBooking?.venueGroup,
      notificationsContext,
      invalidateLinkedBookings
    ]
  );

  const handleUnlinkRequest = useCallback(
    (bookingId: string, reference?: string) => {
      if (!isOnline) {
        notificationsContext.addNotification({
          heading: "Unable to unlink booking whilst offline",
          autoClose: 4000,
          toastType: "error"
        });
        return;
      }
      setUnlinkTarget({ bookingId, reference });
    },
    [isOnline, notificationsContext]
  );

  const handleUnlinkConfirm = useCallback(async () => {
    if (!unlinkTarget) return;

    setIsUnlinking(true);
    try {
      const success = await unlinkBooking(
        selectedBookingId,
        unlinkTarget.bookingId
      );
      if (success) {
        notificationsContext.addNotification({
          heading: "Booking unlinked successfully",
          autoClose: 4000,
          toastType: "success"
        });
        setUnlinkTarget(null);
        invalidateLinkedBookings();
      } else {
        notificationsContext.addNotification({
          heading: "Failed to unlink booking",
          autoClose: 4000,
          toastType: "error"
        });
      }
    } finally {
      setIsUnlinking(false);
    }
  }, [
    unlinkTarget,
    selectedBookingId,
    notificationsContext,
    invalidateLinkedBookings
  ]);

  const handleBulkStatusUpdate = useCallback(
    async (action: LinkedBookingAction) => {
      if (!isOnline) {
        notificationsContext.addNotification({
          heading: "Unable to update bookings whilst offline",
          autoClose: 4000,
          toastType: "error"
        });
        return;
      }

      setIsBulkUpdating(true);
      try {
        const success = await updateLinkedBookingsStatus(
          selectedBookingId,
          action
        );
        if (success) {
          notificationsContext.addNotification({
            heading: "All linked bookings updated successfully",
            autoClose: 4000,
            toastType: "success"
          });
        } else {
          notificationsContext.addNotification({
            heading: "Failed to update linked bookings",
            autoClose: 4000,
            toastType: "error"
          });
        }
        invalidateLinkedBookings();
      } finally {
        setIsBulkUpdating(false);
      }
    },
    [
      isOnline,
      selectedBookingId,
      notificationsContext,
      invalidateLinkedBookings
    ]
  );

  const handleLinkButtonClick = () => {
    if (!isOnline) {
      notificationsContext.addNotification({
        heading: "Unable to link booking whilst offline",
        autoClose: 4000,
        toastType: "error"
      });
      return;
    }
    setIsLinkDialogOpen(true);
  };

  const linkedBookingsTableData = mapLinkedBookingsTableData({
    bookings: linkedBookingsData,
    diaryContext,
    setSelectedTabKey,
    onUnlink: handleUnlinkRequest,
    userCanManageBooking
  });

  const tableColumns = useMemo(
    () => [
      { caption: "DMN Ref #", dataNameJSX: "reference" as const },
      { caption: "Type", dataName: "typeName" as const },
      { caption: "Site", dataName: "site" as const },
      { caption: "Time", dataName: "time" as const },
      { caption: "Date", dataName: "date" as const },
      { caption: "Duration", dataName: "duration" as const },
      { caption: "Status", dataNameJSX: "status" as const },
      { caption: "Area", dataName: "area" as const },
      ...(userCanManageBooking
        ? [{ caption: "", dataNameJSX: "actions" as const }]
        : [])
    ],
    [userCanManageBooking]
  );

  const loading =
    (!linkedBookingsTableData || linkedBookingsTableData.length === 0) &&
    (isLoading || isLoading2) &&
    isOnline;

  const hasMultipleBookings = (linkedBookingsTableData?.length ?? 0) >= 2;

  return (
    <div className="SelectedBookingLinkedContainer mt-panel-padding flex flex-wrap">
      <div className="LinkedBookings-Header flex items-center justify-between m-panel-padding flex-[1_1_100%]">
        <span className="LinkedBookings-Heading font-bold text-[length:var(--fontSize-base)]">
          Linked Bookings
        </span>
        <div className="LinkedBookings-Actions flex items-center gap-3">
          {userCanManageBooking && hasMultipleBookings && (
            <BulkStatusActions
              onExecute={handleBulkStatusUpdate}
              isExecuting={isBulkUpdating}
              disabled={!isOnline}
              bookingCount={linkedBookingsTableData?.length ?? 0}
            />
          )}
          {userCanManageBooking && (
            <EDSButton
              variant="default"
              size="sm"
              onClick={handleLinkButtonClick}
            >
              Link booking
            </EDSButton>
          )}
        </div>
      </div>
      <div className="LinkedBookings mt-panel-padding flex-[1_1_100%] flex flex-col">
        <Table columns={tableColumns} data={linkedBookingsTableData ?? []} />
        {loading && <Loading />}
        {!loading &&
          linkedBookingsTableData &&
          linkedBookingsTableData.length === 0 &&
          diaryContext.selectedBooking && (
            <span className="NoRecords p-panel-padding m-auto">
              No linked bookings found.
            </span>
          )}
      </div>

      <div className="LinkedPayments mt-panel-padding flex-[1_1_100%] flex flex-col">
        {linkedBookingsRefData && (
          <SelectedBookingPayments
            linkedBookingIds={linkedBookingsRefData ?? []}
            setSelectedTabKey={setSelectedTabKey}
          />
        )}
      </div>

      <LinkBookingDialog
        open={isLinkDialogOpen}
        onOpenChange={setIsLinkDialogOpen}
        onConfirm={handleLinkBooking}
        isLinking={isLinking}
      />

      <UnlinkBookingDialog
        open={unlinkTarget !== null}
        onOpenChange={(open) => {
          if (!open) setUnlinkTarget(null);
        }}
        onConfirm={handleUnlinkConfirm}
        isUnlinking={isUnlinking}
        bookingReference={unlinkTarget?.reference}
      />
    </div>
  );
};

export default SelectedBookingLinked;
